import { Box, FormLabel, Textarea, VStack } from '@chakra-ui/react';
import { css } from '@emotion/react';
import { FC, Fragment, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import LinkIcon from '@/assets/icons/ic-link-outer.svg';

import { FilledButton } from '@/components/core/Button/variants/FilledButton';
import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { Field } from '@/components/core/inputs/Field';
import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import { useAnalytics } from '@/context/Analytics';
import { OverlayType } from '@/enums/index';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { Contact, ContactType } from '@/resources/Contact';
import { ButtonVariant } from '@/styles/themes/button';
import { EMAIL_REGEX } from '@/utils/constants';
import { StringParam } from '@/utils/url-query-string';

type FormAttributes = Pick<
  Contact,
  'name' | 'email' | 'message' | 'attendeeCount' | 'eventName' | 'dayCount' | 'lectureCount'
>;

interface IContactFormProps {
  type: ContactType;
}

export const ContactForm: FC<IContactFormProps> = ({ type }) => {
  const { showModalType } = useContext(FadeInModalContext);
  const {
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm();
  const { store } = useStores();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const isConferencesForm = type === ContactType.CONFERENCES;

  const onSubmit = async (attributes: FormAttributes) => {
    await store.request('contact', 'POST', {
      data: {
        type: Contact.type,
        id: '',
        attributes: {
          ...attributes,
          ...(eventId && { event_id: eventId }),
          type,
        },
      },
    });

    if (isConferencesForm) {
      analytics.track('event_organization_form_submit');
    } else {
      analytics.track('contact_form_submit');
    }

    showModalType.set(OverlayType.CONTACT_THANKS);
  };

  return (
    <VStack as="form" align="initial" gap={10} onSubmit={handleSubmit(onSubmit)} w="full">
      <Box>
        <Field
          {...register('name', {
            required: {
              value: true,
              message: t('contactForm.name.required'),
            },
          })}
          label={t('contactForm.name.label')}
          hasValue={Boolean(watch('name'))}
        />
        <ErrorMessage errors={errors} name="name" />
      </Box>

      <Box>
        <Field
          {...register('email', {
            required: {
              value: true,
              message: t('contactForm.email.required'),
            },
            pattern: {
              value: EMAIL_REGEX,
              message: t('contactForm.email.pattern'),
            },
          })}
          type="email"
          label={t('contactForm.email.label')}
          hasValue={Boolean(watch('email'))}
        />
        <ErrorMessage errors={errors} name="email" />
      </Box>

      {isConferencesForm && (
        <Fragment>
          <Box>
            <Field
              {...register('eventName', {
                required: {
                  value: true,
                  message: t('contactForm.eventName.required'),
                },
              })}
              label={t('contactForm.eventName.label')}
              hasValue={Boolean(watch('eventName'))}
            />
            <ErrorMessage errors={errors} name="eventName" />
          </Box>
          <Box>
            <Field
              {...register('dayCount', {
                required: {
                  value: true,
                  message: t('contactForm.dayCount.required'),
                },
              })}
              label={t('contactForm.dayCount.label')}
              hasValue={Boolean(watch('dayCount'))}
            />
            <ErrorMessage errors={errors} name="dayCount" />
          </Box>
          <Box>
            <Field
              {...register('lectureCount')}
              label={t('contactForm.lectureCount.label')}
              hasValue={Boolean(watch('lectureCount'))}
            />
          </Box>
          <Box>
            <Field
              {...register('attendeeCount')}
              label={t('contactForm.attendeeCount.label')}
              hasValue={Boolean(watch('attendeeCount'))}
            />
          </Box>
        </Fragment>
      )}

      <Box>
        <FormLabel fontSize="14px" color="gray.600">
          {t(isConferencesForm ? 'contactForm.message.labelConferences' : 'contactForm.message.label')}
        </FormLabel>
        <Textarea
          {...register('message', {
            required: {
              value: !isConferencesForm,
              message: t('contactForm.message.required'),
            },
          })}
          focusBorderColor="violet.500"
          placeholder={t(
            isConferencesForm ? 'contactForm.message.placeholderConferences' : 'contactForm.message.placeholder'
          )}
          rows={10}
        />
      </Box>

      <ErrorMessage errors={errors} name="message" />
      <FilledButton variant={ButtonVariant.DARK_FILLED} type="submit" css={{ width: 'max-content' }}>
        {t('action.submit')}
        <LinkIcon
          css={css`
            margin-left: 8px;
          `}
        />
      </FilledButton>
    </VStack>
  );
};
